import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'components/StyledButton/StyledButton';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import cloneDeep from 'clone-deep';

import CustomDialog from '../../components/CustomDialog/CustomDialog';
import { Props as CustomInputProps } from '../../components/CustomInput/CustomInput';
import Deviation from '../../types/Deviation';
import PlasticAssessmentItem, { EmptyPlasticAssessmentItem } from '../../types/PlasticAssessmentItem';
import api from '../../utils/api';
import { setNestedKey } from '../../utils/immutability';
import snackbar from '../../utils/snackbar';
import Form from './components/Form/Form';

/*
 * This dialog contains the deviation data details form.
 */

interface Props {
    open: boolean;
    onClose: (dataId?: number) => void;
    companyId: number;
    dataId: number;
    getDeviationDescription: (deviation: Deviation) => string;
    isCustomer: boolean;
}

const DeviationDataDetailsDialog = (props: Props) => {
    const { open, onClose, companyId, dataId, getDeviationDescription, isCustomer } = props;

    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState(false);
    const [itemData, setItemData] = useState<PlasticAssessmentItem>(EmptyPlasticAssessmentItem);

    const loadData = useCallback(() => {
        // fetch deviation data
        setIsLoading(true);
        api.request('/companies/' + companyId + '/deviations/' + dataId).then((res) => {
            setItemData(res);
        }).finally(() => {
            setIsLoading(false);
        });
    }, [companyId, dataId]);

    const handleAttributeChange = useCallback((attribute: string, value: any) => {
        // set the new value in the state
        setItemData((curr: any) => {
            const newState = cloneDeep(curr);
            setNestedKey(newState, attribute.split('.'), value);

            if (attribute === 'deviationPresent' && value === '0') {
                // clear the deviation count if deviation is not present
                setNestedKey(newState, ['deviationCount'], null);
            }

            return newState;
        });
    }, []);

    const handleSubmit = useCallback(() => {
        // called on form submit, send the data to the API

        if (!isCustomer) return;

        setIsLoading(true);

        api.request('/companies/' + companyId + '/deviations/' + dataId, 'PUT', itemData).then((res) => {
            snackbar.success(t('dialogs.deviation_data_details.deviation_saved'));
            onClose(res.id);
        }).finally(() => {
            setIsLoading(false);
        });
    }, [companyId, itemData, dataId, isCustomer, onClose, t]);

    const handleKeyDown = useCallback((e) => {
        // on enter key press, submit the form
        if (e.keyCode === 13 && !isLoading) {
            handleSubmit();
        }
    }, [handleSubmit, isLoading]);

    useEffect(() => {
        if (!open) return;

        setItemData(EmptyPlasticAssessmentItem);
        loadData();
    }, [loadData, open]);

    const baseInputProps: Omit<CustomInputProps, 'name'> = {
        data: itemData,
        disabled: isLoading,
        partialTranslationKey: 'models.deviation_data.attributes',
        handleAttributeChange,
        handleKeyDown,
        ...(!isCustomer && {
            // non customer users cannot modify the data
            InputProps: {
                readOnly: true,
            }
        })
    };

    return (
        <>
            <CustomDialog onClose={() => onClose()} open={open} fullWidth alwaysVisible maxWidth='lg'>
                <DialogTitle className='dialog-title-text-ellipsis'>
                    {t('dialogs.deviation_data_details.title')}
                </DialogTitle>

                <DialogContent dividers>
                    <Form baseInputProps={baseInputProps} getDeviationDescription={getDeviationDescription} />
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => onClose()} color='secondary' disabled={isLoading} >
                        {t('dialogs.generic.close')}
                    </Button>

                    {isCustomer && <Button onClick={() => handleSubmit()} color='primary' disabled={isLoading} >
                        {t('dialogs.generic.save')}
                    </Button>}
                </DialogActions>
            </CustomDialog>
        </>
    );
};

export default DeviationDataDetailsDialog;
